import { useEffect, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import loadable from '@loadable/component';
import classNames from 'classnames';

import { type AddVoucher, CustomUIEventEnum, type RemoveVoucher } from '@jsmdg/ui-event';
import { Link, Link as LinkAsButton, LinkVariant, RenderType } from '@jsmdg/yoshi';
import { type Money, type Price as PriceType } from '../../../shared/types';
import { formatPrice } from '../../helper/formatPrice';
import { PurchaseType } from '../../types/enums/purchaseType';
import { FriendsDiscountInfo } from './FriendsDiscountInfo';
import { trackShippingTextClicked } from './price.tracking';
import styles from './Price.module.scss';

const LoadableStrikePriceModal = loadable(async () => import('./StrikePriceModal'));
const LoadableShippingModal = loadable(async () => import('../ShippingModal/ShippingModal'));

type PriceProps = {
    readonly price: PriceType;
    readonly originalPrice?: PriceType;
    readonly multiplier?: number;
    readonly vouchersTotal?: Money;
    readonly hasFriendsDiscount: boolean;
    readonly isDisabled?: boolean;
    readonly purchaseType?: string;
};

const messages = defineMessages({
    shippingModalTexLink: {
        defaultMessage: 'zzgl. Versand',
    },
    credit: {
        defaultMessage: 'Guthaben',
    },
});

const Price = ({
    hasFriendsDiscount,
    isDisabled = false,
    multiplier = 1,
    originalPrice,
    price,
    purchaseType,
    vouchersTotal,
}: PriceProps): JSX.Element => {
    const intl = useIntl();
    const [isShippingModalOpen, setIsShippingModalOpen] = useState(false);
    const [credit, setCredit] = useState<Money | undefined>(vouchersTotal);
    const [showStrikePriceModal, setShowStrikePriceModal] = useState(false);

    useEffect(() => {
        const handleVoucherUIEvent = (event: CustomEvent<AddVoucher | RemoveVoucher>) => {
            if (event.detail.amount > 0) {
                setCredit(event.detail);
            } else {
                setCredit(undefined);
            }
        };

        document.addEventListener(CustomUIEventEnum.AddVoucher, handleVoucherUIEvent);
        document.addEventListener(CustomUIEventEnum.RemoveVoucher, handleVoucherUIEvent);

        return () => {
            document.removeEventListener(CustomUIEventEnum.AddVoucher, handleVoucherUIEvent);
            document.removeEventListener(CustomUIEventEnum.RemoveVoucher, handleVoucherUIEvent);
        };
    }, []);

    const handleShippingTextClick = (): void => {
        if (isDisabled) {
            return;
        }

        setIsShippingModalOpen(true);
        trackShippingTextClicked();
    };

    const useFriendsDiscount =
        purchaseType === PurchaseType.Voucher && hasFriendsDiscount && multiplier > 1;

    return (
        <>
            <span
                data-original-price={!!originalPrice}
                className={classNames('theme-text-display-1 mr-1x d-inline-block', styles.price)}
                data-testid="price"
            >
                {formatPrice(
                    intl.locale,
                    Number(price.gross.amount) * multiplier,
                    price.gross.currencyCode,
                )}
            </span>
            {!!originalPrice && (
                <>
                    <span
                        className={classNames(
                            styles.originalPrice,
                            'mr-0-5x text-decoration-line-through',
                        )}
                    >
                        {formatPrice(
                            intl.locale,
                            originalPrice.gross.amount * multiplier,
                            originalPrice.gross.currencyCode,
                        )}
                    </span>
                    <Link
                        onClick={() => setShowStrikePriceModal(true)}
                        variant={LinkVariant.Brand}
                        className="text-decoration-none"
                    >
                        *
                    </Link>
                </>
            )}
            {credit && (
                <div
                    className={classNames(
                        styles.credit,
                        'd-inline-block fw-bold font-complementary',
                    )}
                >
                    <span>{intl.formatMessage(messages.credit)}</span>
                    &nbsp;
                    <span>
                        {intl.formatNumber(credit.amount, {
                            style: 'currency',
                            currency: credit.currencyCode,
                        })}
                    </span>
                </div>
            )}
            {useFriendsDiscount && <FriendsDiscountInfo price={price} multiplier={multiplier} />}
            <div className={`theme-text-caption-text mt-sm-1x mr-sm-1-5x ${styles.info}`}>
                <FormattedMessage
                    defaultMessage="{shipping} (inkl. MwSt.)"
                    values={{
                        shipping: (
                            <LinkAsButton
                                onClick={handleShippingTextClick}
                                as={RenderType.Button}
                                variant={LinkVariant.Grey}
                            >
                                {intl.formatMessage(messages.shippingModalTexLink)}
                            </LinkAsButton>
                        ),
                    }}
                />
            </div>
            {isShippingModalOpen && (
                <LoadableShippingModal
                    isOpen={isShippingModalOpen}
                    onRequestClose={() => setIsShippingModalOpen(false)}
                />
            )}
            {showStrikePriceModal && (
                <LoadableStrikePriceModal
                    isOpen={showStrikePriceModal}
                    handleOnClose={() => setShowStrikePriceModal(false)}
                    numTickets={multiplier}
                />
            )}
        </>
    );
};

export { Price };
